/**
 * Derive post-level primary_destination from a Google-backed Location (POI).
 * Uses the same identifying-name rules as City.identifyingNameFromGooglePlacesData.
 */

import City, { type GooglePlacesData } from '@/models/city';
import type Location from '@/models/location';
import type { PlaceCardData } from '@/types/listViewTypes';
import type { PrimaryDestination } from '@/types/city';

function readGeopositionFromLocationRecord(
  loc: Record<string, unknown>,
): PrimaryDestination['geoposition'] | undefined {
  const geo = loc.geoposition as { type?: string; coordinates?: unknown[] } | undefined;
  if (geo?.coordinates?.length === 2) {
    const [lng, lat] = geo.coordinates;
    if (typeof lng === 'number' && typeof lat === 'number' && Number.isFinite(lng) && Number.isFinite(lat)) {
      return { type: 'Point', coordinates: [lng, lat] };
    }
  }

  const gp = loc.google_places_data as Record<string, unknown> | undefined;
  const pt = gp?.location as Record<string, unknown> | undefined;
  if (!pt) return undefined;

  const parseNum = (v: unknown): number | undefined => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (v && typeof v === 'object' && '$numberDouble' in v) {
      const n = Number((v as { $numberDouble: string }).$numberDouble);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };

  const lat = parseNum(pt.latitude);
  const lng = parseNum(pt.longitude);
  if (lat === undefined || lng === undefined) return undefined;
  return { type: 'Point', coordinates: [lng, lat] };
}

/**
 * Builds a city-type primary destination for a place Location when Google data
 * or a3_intl_address yields a stable identifying string.
 */
export function primaryDestinationFromGoogleLocation(
  location: Location | Record<string, unknown> | null | undefined,
): PrimaryDestination | null {
  if (!location) return null;

  const loc = location as Record<string, unknown>;
  const gp = loc.google_places_data as GooglePlacesData | null | undefined;

  let identifying = '';
  if (gp && typeof gp === 'object') {
    identifying = (City.identifyingNameFromGooglePlacesData(gp as GooglePlacesData) || '').trim();
  }

  if (!identifying) {
    const a3 = typeof loc.a3_intl_address === 'string' ? loc.a3_intl_address.trim() : '';
    if (a3) identifying = a3;
  }

  if (!identifying) return null;

  return {
    type: 'city',
    identifying_name: identifying,
    geoposition: readGeopositionFromLocationRecord(loc),
  };
}

/**
 * Derive a city-level primary destination from PlaceCardData.
 * Best effort: prefer explicit cityName, then infer from trailing address tokens.
 */
export function primaryDestinationFromPlaceCard(
  place: PlaceCardData | null | undefined,
): PrimaryDestination | null {
  if (!place) return null;

  const city = place.cityName?.trim() || '';
  const address = place.address?.trim() || '';
  const addressParts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  let identifyingName = '';
  if (city) {
    const tail = addressParts.at(-1);
    if (tail && tail.toLowerCase() !== city.toLowerCase()) {
      identifyingName = `${city}, ${tail}`;
    } else {
      identifyingName = city;
    }
  } else if (addressParts.length >= 2) {
    identifyingName = `${addressParts[addressParts.length - 2]}, ${addressParts[addressParts.length - 1]}`;
  } else if (addressParts.length === 1) {
    identifyingName = addressParts[0];
  }

  const hasCoordinates = typeof place.longitude === 'number' && typeof place.latitude === 'number'
    && Number.isFinite(place.longitude)
    && Number.isFinite(place.latitude);

  if (!identifyingName) return null;

  return {
    type: 'city',
    identifying_name: identifyingName,
    ...(hasCoordinates
      ? {
        geoposition: {
          type: 'Point' as const,
          coordinates: [place.longitude, place.latitude] as [number, number],
        },
      }
      : {}),
  };
}
